import { useCallback, useEffect, useRef, useState } from 'react';
import type {
    CalendarPublication,
    CalendarPublicationHoverAnchor,
    PopoverPosition,
} from '../types';

const PUBLICATION_PREVIEW_OPEN_MS = 80;
const PUBLICATION_PREVIEW_CLOSE_MS = 220;
const DETAIL_POPOVER_WIDTH = 380;
const DETAIL_POPOVER_CELL_GAP = 8;

type DetailPopover = Readonly<{
    publication: CalendarPublication;
    position: PopoverPosition;
}>;

type DayPopover = Readonly<{
    day: Date;
    position: PopoverPosition;
}>;

function computeDetailPopoverPosition(anchor: CalendarPublicationHoverAnchor): PopoverPosition {
    const { clientX, clientY, cellRect } = anchor;
    const top = clientY + DETAIL_POPOVER_CELL_GAP;
    if (cellRect) {
        const width = Math.min(window.innerWidth - 24, DETAIL_POPOVER_WIDTH);
        const left = cellRect.left - width - DETAIL_POPOVER_CELL_GAP;
        return { top, left };
    }
    return { top, left: clientX - 160 };
}

export type PublicationPreviewHandlers = Readonly<{
    detailPopover: DetailPopover | null;
    dayPopover: DayPopover | null;
    openPublicationPreview: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    schedulePublicationPreview: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    handlePublicationTriggerLeave: () => void;
    cancelClosePublicationPreview: () => void;
    schedulePublicationClose: () => void;
    openDayList: (day: Date, top: number, left: number) => void;
    closeDetailPopover: () => void;
    closeAll: () => void;
}>;

export function usePublicationPreview(): PublicationPreviewHandlers {
    const [detailPopover, setDetailPopover] = useState<DetailPopover | null>(null);
    const [dayPopover, setDayPopover] = useState<DayPopover | null>(null);
    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearOpenTimer = useCallback(() => {
        if (openTimerRef.current !== null) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
    }, []);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current !== null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearOpenTimer();
            clearCloseTimer();
        };
    }, [clearOpenTimer, clearCloseTimer]);

    const openPublicationPreview = useCallback(
        (publication: CalendarPublication, anchor: CalendarPublicationHoverAnchor) => {
            clearOpenTimer();
            clearCloseTimer();
            if (!anchor.keepDayListOpen) {
                setDayPopover(null);
            }
            setDetailPopover({
                publication,
                position: computeDetailPopoverPosition(anchor),
            });
        },
        [clearOpenTimer, clearCloseTimer],
    );

    const schedulePublicationPreview = useCallback(
        (publication: CalendarPublication, anchor: CalendarPublicationHoverAnchor) => {
            clearOpenTimer();
            clearCloseTimer();
            openTimerRef.current = setTimeout(() => {
                openTimerRef.current = null;
                openPublicationPreview(publication, anchor);
            }, PUBLICATION_PREVIEW_OPEN_MS);
        },
        [clearOpenTimer, clearCloseTimer, openPublicationPreview],
    );

    const schedulePublicationClose = useCallback(() => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setDetailPopover(null);
        }, PUBLICATION_PREVIEW_CLOSE_MS);
    }, [clearCloseTimer]);

    const handlePublicationTriggerLeave = useCallback(() => {
        clearOpenTimer();
        schedulePublicationClose();
    }, [clearOpenTimer, schedulePublicationClose]);

    const openDayList = useCallback(
        (day: Date, top: number, left: number) => {
            clearOpenTimer();
            clearCloseTimer();
            setDetailPopover(null);
            setDayPopover({ day, position: { top, left } });
        },
        [clearOpenTimer, clearCloseTimer],
    );

    const closeDetailPopover = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        setDetailPopover(null);
    }, [clearOpenTimer, clearCloseTimer]);

    const closeAll = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        setDetailPopover(null);
        setDayPopover(null);
    }, [clearOpenTimer, clearCloseTimer]);

    return {
        detailPopover,
        dayPopover,
        openPublicationPreview,
        schedulePublicationPreview,
        handlePublicationTriggerLeave,
        cancelClosePublicationPreview: clearCloseTimer,
        schedulePublicationClose,
        openDayList,
        closeDetailPopover,
        closeAll,
    };
}
